/**
 * "Can the browser reach the Mindstone API over HTTP?" — a module-level
 * signal fed by the Apollo links in `~/graphql/api-connectivity-link.ts` and read by the
 * notice in `~/components/containers/api-connectivity-notice.tsx`.
 *
 * Separate from `connectivity.ts` on purpose: that one follows the WebSocket
 * lifecycle, which says nothing about HTTP (a corporate proxy or an ad blocker
 * can pass one and block the other).
 *
 * An individual fetch failure does not prove a general outage or identify
 * its cause. Confirm with a read-only HTTP probe before warning, and check
 * for recovery even when the learner stays on an otherwise idle page.
 *
 * Browser-only state: on the server every request must start "reachable".
 */

/** No HTTP response, or a gateway answering 502/503/504. */
export type ApiUnreachableReason = "network" | "gateway";

let reachable = true;
let reason: ApiUnreachableReason = "network";
const listeners = new Set<() => void>();
const episodeListeners = new Set<
  (operationName: string, reason: ApiUnreachableReason) => void
>();
export type ApiConnectivityObservation =
  | {
      kind: "probe_started";
      operationName: string;
      generation: number;
    }
  | {
      kind: "probe_finished";
      operationName: string;
      generation: number;
      result:
        | "success"
        | "stale"
        | "gateway"
        | "network"
        | "invalid_response"
        | "timeout"
        | "cancelled";
    }
  | {
      kind: "outage_started";
      operationName: string;
      reason: ApiUnreachableReason;
    }
  | { kind: "outage_recovered" };
const diagnosticListeners = new Set<
  (observation: ApiConnectivityObservation) => void
>();

const isBrowser = () => typeof window !== "undefined";

const PROBE_TIMEOUT_MS = 5_000;
const RECOVERY_INTERVAL_MS = 15_000;
let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
export type ApiReachabilityHeaders = (
  signal: AbortSignal,
) => Promise<HeadersInit>;

let failureGeneration = 0;
let activeProbe:
  | {
      controller: AbortController;
      timeout: ReturnType<typeof setTimeout>;
      operationName: string;
      generation: number;
    }
  | undefined;

function notifyDiagnostics(observation: ApiConnectivityObservation) {
  diagnosticListeners.forEach((listener) => {
    try {
      listener(observation);
    } catch {
      // New diagnostic observers are fail-open; established listeners are unchanged.
    }
  });
}

function cancelReachabilityChecks() {
  clearTimeout(recoveryTimer);
  recoveryTimer = undefined;
  if (activeProbe) {
    const probe = activeProbe;
    activeProbe = undefined;
    clearTimeout(probe.timeout);
    probe.controller.abort();
    notifyDiagnostics({
      kind: "probe_finished",
      operationName: probe.operationName,
      generation: probe.generation,
      result: "cancelled",
    });
  }
}

/** One read-only check at a time; never retry the operation that failed. */
export function confirmApiUnreachable(
  operationName = "unknown",
  graphqlUrl: string,
  getHeaders?: ApiReachabilityHeaders,
) {
  if (!isBrowser()) return;
  // Even a deduplicated new failure invalidates an older probe's success.
  failureGeneration++;
  if (activeProbe || recoveryTimer !== undefined) return;
  startProbe(operationName, graphqlUrl, getHeaders);
}

function startProbe(
  operationName: string,
  graphqlUrl: string,
  getHeaders?: ApiReachabilityHeaders,
) {
  const generation = failureGeneration;
  const controller = new AbortController();
  const probe = {
    controller,
    operationName,
    generation,
    timeout: setTimeout(
      () => finish("network", "timeout"),
      PROBE_TIMEOUT_MS,
    ),
  };
  activeProbe = probe;
  notifyDiagnostics({ kind: "probe_started", operationName, generation });

  function finish(
    failure: ApiUnreachableReason | null,
    result: Extract<
      ApiConnectivityObservation,
      { kind: "probe_finished" }
    >["result"],
  ) {
    // A newer HTTP success, reset or timeout invalidates this result.
    if (activeProbe !== probe) return;
    activeProbe = undefined;
    clearTimeout(probe.timeout);
    controller.abort();
    if (failure === null) {
      if (generation === failureGeneration) {
        notifyDiagnostics({
          kind: "probe_finished",
          operationName,
          generation,
          result: "success",
        });
        reportApiReachable();
      } else {
        // A newer real failure needs its own confirmation. A stale success
        // proves neither an outage nor recovery: preserve the current state.
        notifyDiagnostics({
          kind: "probe_finished",
          operationName,
          generation,
          result: "stale",
        });
        startProbe(operationName, graphqlUrl, getHeaders);
      }
      return;
    }
    notifyDiagnostics({
      kind: "probe_finished",
      operationName,
      generation,
      result,
    });
    recoveryTimer = setTimeout(() => {
      recoveryTimer = undefined;
      startProbe(operationName, graphqlUrl, getHeaders);
    }, RECOVERY_INTERVAL_MS);
    reportApiUnreachable(operationName, failure);
  }

  // Match the application's POST transport and live bearer auth, without Apollo
  // recursion. The deadline covers auth, fetch and response-body consumption.
  void (async () => {
    try {
      const headers = new Headers(
        getHeaders ? await getHeaders(controller.signal) : undefined,
      );
      if (activeProbe !== probe) return;
      headers.set("content-type", "application/json");
      const response = await fetch(graphqlUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
          operationName: "ApiReachability",
          query: "query ApiReachability { __typename }",
        }),
        cache: "no-store",
        credentials: "omit",
        signal: controller.signal,
      });
      if (isGatewayUnavailableStatus(response.status)) {
        finish("gateway", "gateway");
        return;
      }
      // A login page or other intermediary can return 200. Only a GraphQL
      // response proves this transport works; auth/business errors are reachable.
      const body: unknown = await response.json();
      const isGraphqlResponse = isGraphqlProbeResponse(body);
      finish(
        isGraphqlResponse ? null : "network",
        isGraphqlResponse ? "success" : "invalid_response",
      );
    } catch {
      finish("network", "network");
    }
  })();
}

function isGraphqlProbeResponse(body: unknown): boolean {
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  const { data, errors } = body as { data?: unknown; errors?: unknown };
  if (
    data &&
    typeof data === "object" &&
    "__typename" in data &&
    data.__typename === "Query"
  )
    return true;
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every(
      (error: unknown) =>
        error !== null &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string",
    )
  );
}

export function isApiReachable(): boolean {
  return reachable;
}

export function getApiUnreachableReason(): ApiUnreachableReason {
  return reason;
}

export function subscribeApiReachability(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Fires once per unreachable episode (the reachable → unreachable transition). */
export function onApiUnreachableEpisode(
  listener: (operationName: string, reason: ApiUnreachableReason) => void,
): () => void {
  episodeListeners.add(listener);
  return () => {
    episodeListeners.delete(listener);
  };
}

/** Browser diagnostic lifecycle only; observer failures never affect reachability. */
export function onApiConnectivityObservation(
  listener: (observation: ApiConnectivityObservation) => void,
): () => void {
  diagnosticListeners.add(listener);
  return () => {
    diagnosticListeners.delete(listener);
  };
}

export function reportApiUnreachable(
  operationName = "unknown",
  why: ApiUnreachableReason = "network",
) {
  if (!isBrowser()) return;
  if (!reachable) return;
  reachable = false;
  reason = why;
  notifyDiagnostics({
    kind: "outage_started",
    operationName,
    reason: why,
  });
  episodeListeners.forEach((listener) => listener(operationName, why));
  listeners.forEach((listener) => listener());
}

export function reportApiReachable() {
  if (!isBrowser()) return;
  cancelReachabilityChecks();
  if (reachable) return;
  reachable = true;
  notifyDiagnostics({ kind: "outage_recovered" });
  listeners.forEach((listener) => listener());
}

/**
 * The browser could not get a response at all: Chrome "Failed to fetch",
 * Safari "Load failed", Firefox "NetworkError when attempting to fetch
 * resource", Node/undici "fetch failed", or the browser says it is offline.
 * Deliberately narrow — an AbortError (navigation), a non-2xx response, or a
 * parse failure are not "unreachable".
 */
export function isNetworkUnreachableError(error: unknown): boolean {
  // Browser-only by construction: a server-side undici "fetch failed" is a
  // genuine in-container outage and must keep reaching Sentry as an error.
  if (!isBrowser()) return false;
  if (!(error instanceof Error)) return false;
  if (error.name === "AbortError") return false;
  if (!(error instanceof TypeError)) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false)
    return true;
  const message = error.message;
  return (
    message.startsWith("Failed to fetch") ||
    message.startsWith("Load failed") ||
    message.startsWith("NetworkError when attempting to fetch resource") ||
    message.startsWith("fetch failed")
  );
}

/** A gateway that cannot reach the API is, for the learner, the same thing. */
export function isGatewayUnavailableStatus(
  statusCode: number | undefined,
): boolean {
  return statusCode === 502 || statusCode === 503 || statusCode === 504;
}

/** Test seam. */
export function resetApiReachabilityForTests() {
  cancelReachabilityChecks();
  reachable = true;
  failureGeneration = 0;
  reason = "network";
  listeners.clear();
  episodeListeners.clear();
  diagnosticListeners.clear();
}
